import { z } from 'zod';
import { SortQuerySchema } from '../common';

const moneyRegex = /^\d+(\.\d{1,2})?$/;

const categoryCodeRegex = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/;

const optionalBooleanQuery = z.preprocess((value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
}, z.boolean().optional());

const optionalParentId = z
  .preprocess((value) => (value === '' ? null : value), z.number().int().positive().nullable())
  .optional();

export const CatalogCategoryBehaviorSchema = z
  .object({
    version: z.number().int().positive().default(1),
    kind: z.enum(['standard', 'hierarchical', 'system']).default('standard'),
    visibleInHub: z.boolean().default(true),
    hierarchy: z
      .object({
        enabled: z.boolean().default(false),
        maxDepth: z.number().int().positive().optional(),
        display: z.enum(['table', 'tree']).default('table'),
      })
      .default({ enabled: false, display: 'table' }),
    lifecycle: z
      .object({
        allowDeactivate: z.boolean().default(true),
        allowHardDelete: z.boolean().default(true),
      })
      .default({ allowDeactivate: true, allowHardDelete: true }),
    selection: z
      .object({
        activeOnly: z.boolean().default(true),
      })
      .default({ activeOnly: true }),
    specialBehavior: z.enum(['none', 'system-flag', 'status']).default('none'),
  })
  .passthrough();

export const CreateCatalogCategoryDtoSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(categoryCodeRegex, 'code must use uppercase letters, numbers, and underscores'),
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(1000).nullable().optional(),
  parentCategoryCode: z.string().trim().regex(categoryCodeRegex).nullable().optional(),
  behavior: CatalogCategoryBehaviorSchema.optional(),
  active: z.boolean().optional(),
});

export type CreateCatalogCategoryDto = z.infer<typeof CreateCatalogCategoryDtoSchema>;

export const UpdateCatalogCategoryDtoSchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  description: z.string().trim().max(1000).nullable().optional(),
  parentCategoryCode: z.string().trim().regex(categoryCodeRegex).nullable().optional(),
  behavior: CatalogCategoryBehaviorSchema.optional(),
  active: z.boolean().optional(),
});

export type UpdateCatalogCategoryDto = z.infer<typeof UpdateCatalogCategoryDtoSchema>;

export const CreateCatalogoDtoSchema = z.object({
  key: z.string().trim().min(1),
  value: z.string().trim().min(1),
  category: z.string().trim().min(1),
  parent: optionalParentId,
  active: z.boolean().optional(),
});

export type CreateCatalogoDto = z.infer<typeof CreateCatalogoDtoSchema>;

export const UpdateCatalogoDtoSchema = z.object({
  key: z.string().trim().min(1).optional(),
  value: z.string().trim().min(1).optional(),
  category: z.string().trim().min(1).optional(),
  parent: optionalParentId,
  active: z.boolean().optional(),
});

export type UpdateCatalogoDto = z.infer<typeof UpdateCatalogoDtoSchema>;

export const QueryCatalogosDtoSchema = z
  .object({
    offset: z.coerce.number().int().min(0).default(0),
    // Límite 1000: el catálogo MUNICIPIO tiene ~340 entradas y la cascada
    // departamento → municipio se resuelve client-side.
    limit: z.coerce.number().int().min(1).max(1000).default(20),
    search: z.string().optional(),
    category: z.string().optional(),
    parentId: z.coerce.number().int().positive().optional(),
    active: optionalBooleanQuery,
  })
  .merge(SortQuerySchema);

export type QueryCatalogosDto = z.infer<typeof QueryCatalogosDtoSchema>;

export const CreatePlanDtoSchema = z.object({
  nombre: z.string().min(1),
  precio: z
    .string()
    .regex(moneyRegex, 'precio must be a valid money value (max 2 decimals)')
    .optional(),
  comision: z
    .string()
    .regex(moneyRegex, 'comision must be a valid money value (max 2 decimals)')
    .optional(),
  generaComision: z.boolean().optional(),
  comentarios: z.string().optional(),
});

export type CreatePlanDto = z.infer<typeof CreatePlanDtoSchema>;

export const UpdatePlanDtoSchema = z.object({
  nombre: z.string().optional(),
  precio: z
    .string()
    .regex(moneyRegex, 'precio must be a valid money value (max 2 decimals)')
    .optional(),
  comision: z
    .string()
    .regex(moneyRegex, 'comision must be a valid money value (max 2 decimals)')
    .optional(),
  generaComision: z.boolean().optional(),
  comentarios: z.string().optional(),
});

export type UpdatePlanDto = z.infer<typeof UpdatePlanDtoSchema>;

export const QueryPlanesDtoSchema = z
  .object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    search: z.string().optional(),
  })
  .merge(SortQuerySchema);

export type QueryPlanesDto = z.infer<typeof QueryPlanesDtoSchema>;

export const CreateTipoServicioDtoSchema = z.object({
  nombre: z.string().min(1),
  generaCobro: z.boolean().optional(),
});

export type CreateTipoServicioDto = z.infer<typeof CreateTipoServicioDtoSchema>;

export const UpdateTipoServicioDtoSchema = z.object({
  nombre: z.string().optional(),
  generaCobro: z.boolean().optional(),
});

export type UpdateTipoServicioDto = z.infer<typeof UpdateTipoServicioDtoSchema>;

export const QueryTiposServicioDtoSchema = z
  .object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    search: z.string().optional(),
  })
  .merge(SortQuerySchema);

export type QueryTiposServicioDto = z.infer<typeof QueryTiposServicioDtoSchema>;
